import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import menuUploaderV2Service, {
  isV4Output,
  type BackendAIResult,
  type BackendV4Output,
} from '@services/menuUploaderV2Service';
import type { DetectedSection } from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { ACCEPTED_PDF_MIME } from '../constants';
import type { FileKind, UploadQueueItem } from '../types';
import { classifyError } from '../utils/classifyError';
import { savePendingJob, clearPendingJob, type PendingJob } from '../utils/pendingJob';

/**
 * Adaptador inline V4 → shape V3 (`BackendAIResult`) para que el `consolidate`
 * downstream — que sólo conoce V3 — funcione sin cambios al recibir un payload
 * V4.
 *
 * Caveats:
 * - V4 trae `confidence: { score, reason }` por entidad; V3 lo expone como
 *   `number`. Aplanamos a `score` y descartamos `reason`.
 * - V4 no incluye `originalText`; usamos `nombre` como fallback razonable.
 * - V4 trae `applicableToCategories`/`appliesTo` con shape rica; reducimos al
 *   campo `applicableToCategories: string[]` que espera V3. Los sections + sus
 *   refs cross-entity van por el campo `sections` separado de `UploadQueueItem`.
 *
 * @param {BackendV4Output} v4 Payload V4 emitido por el backend (schemaVersion '4.0').
 * @returns {BackendAIResult} Mismo contenido en shape V3 (sin sections).
 */
const v4ToBackendAIResult = (v4: BackendV4Output): BackendAIResult => ({
  overallConfidence: 0,
  categories: v4.categories.map((cat) => ({
    id: cat.id,
    nombre: cat.nombre,
    confidence: cat.confidence.score,
    originalText: cat.nombre,
  })),
  products: v4.products.map((prod) => ({
    id: prod.id,
    nombre: prod.nombre,
    descripcion: prod.descripcion,
    confidence: prod.confidence.score,
    originalText: prod.nombre,
    categoryId: prod.categoryId,
    opciones: prod.opciones.map((op) => ({
      presentacion: op.presentacion,
      precio: op.precio,
      costo_produccion: op.costo_produccion,
      // V3 espera `confidence` por opción; V4 sólo trae confidence a nivel producto.
      confidence: prod.confidence.score,
    })),
  })),
  extras: v4.extras.map((extra) => ({
    id: extra.id,
    nombre: extra.nombre,
    precio: extra.precio,
    costo_produccion: extra.costo_produccion,
    confidence: extra.confidence.score,
    originalText: extra.nombre,
    applicableToCategories: extra.appliesTo.categoryIds,
  })),
  suggestions: v4.meta.notes,
});

/**
 * Mapea `BackendV4Output.sections[]` a `DetectedSection[]` aplicando el mismo
 * esquema de prefijo `${batchId}__${originalId}` que usa `consolidate.ts` para
 * categories/products/extras. Con single-call no hay colisiones cross-archivo
 * (el backend consolida internamente), pero mantenemos el prefijo por
 * consistencia con el resto del pipeline downstream.
 *
 * @param {string} batchId ID del batch (espacio de nombres uniforme).
 * @param {BackendV4Output} v4 Payload V4 del backend.
 * @returns {DetectedSection[]} Sections con IDs y refs prefijados.
 */
const prefixV4Sections = (batchId: string, v4: BackendV4Output): DetectedSection[] => {
  const prefix = (originalId: string): string => `${batchId}__${originalId}`;
  return v4.sections.map((sec) => ({
    id: prefix(sec.id),
    name: sec.nombre,
    confidence: sec.confidence.score,
    originalText: sec.nombre,
    type: 'section' as const,
    numero_de_opciones: sec.numero_de_opciones,
    tipo: sec.tipo,
    obligatorio: sec.obligatorio,
    es_multiplo: sec.es_multiplo ? 'multiple' : 'simple',
    opciones: sec.opciones.map((extId) => prefix(extId)),
    applicableToCategories: sec.applicableToCategories.map((catId) => prefix(catId)),
    applicableToProducts: sec.applicableToProducts.map((prodId) => prefix(prodId)),
  }));
};

export interface UseMultiFileMenuProcessingOptions {
  /**
   * @deprecated Single-call flow: no aplica concurrencia per-file.
   * Mantenido para compat de firma con callers que aún lo pasan; ignorado.
   */
  maxConcurrent?: number;
  /**
   * @deprecated Single-call flow: el timeout efectivo lo controla el callable
   * Firebase (900_000 ms en `processMenuV4`). Mantenido para compat.
   */
  timeoutMs?: number;
  /**
   * @deprecated Single-call flow: no hay retry automático interno. El usuario
   * reintenta con `retry()` si falla. Mantenido para compat.
   */
  retryMax?: number;
}

export interface UseMultiFileMenuProcessingReturn {
  /**
   * Lista de items. Cada entrada es UN batch completo (single-call). El batch
   * principal arranca con `enqueue`/`resume` (índice 0); los batches ADITIVOS
   * lanzados con `appendBatch` (desde review) se agregan al final, así que la
   * cardinalidad real es 0..N. El `file` de cada item es el PRIMER archivo del
   * batch (representante para UI).
   */
  items: UploadQueueItem[];
  /**
   * Progreso agregado de los batches. Semántica:
   * - `total`: número de batches lanzados (no de archivos).
   * - `done`: batches que terminaron OK.
   * - `failed`: batches que fallaron.
   */
  totalProgress: { done: number; total: number; failed: number };
  isProcessing: boolean;
  /** Arranca un nuevo batch principal. Reemplaza cualquier batch previo en la cola. */
  enqueue: (files: File[]) => void;
  /**
   * Procesa un batch ADICIONAL sin destruir los items existentes (review:
   * "+ Agregar fotos o PDF"). El item nuevo sigue el mismo ciclo
   * uploading → analyzing → completed/failed que el principal.
   *
   * Tradeoff documentado: los appends NO persisten `pendingJob` — la referencia
   * recuperable post-reload sigue siendo la del batch PRINCIPAL. Un reload
   * durante un append recupera el batch principal + el draft de curaduría, pero
   * los archivos del append deben volver a subirse. Persistir N jobs requeriría
   * un recovery multi-job que no existe en esta versión.
   *
   * @returns El id del batch creado (clave para `retry`/`discardBatch` y para
   * que el caller lleve su ledger de archivos por batch), o null si `files`
   * está vacío.
   */
  appendBatch: (files: File[]) => string | null;
  /** Cancela y remueve TODO (id ignorado; semántica legacy del batch único). */
  remove: (id: string) => void;
  /**
   * Reintenta un batch. Si `id` corresponde a un batch aditivo fallido, lo
   * relanza con sus mismos archivos conservando el id (los demás items no se
   * tocan). Con cualquier otro id (legacy '' / 'ignored'), reintenta el último
   * batch principal o, si lo último fue un resume por jobId, re-consulta ese jobId.
   */
  retry: (id: string) => void;
  /** Remueve UN batch por id (descartar un append fallido) sin tocar el resto. */
  discardBatch: (id: string) => void;
  /** Cancela todos los batches activos. */
  abortAll: () => void;
  /** Limpia todo el estado y vuelve a idle. */
  reset: () => void;
  /**
   * Retoma un job persistido tras un reload de página: crea un item sintético
   * en 'analyzing' (sin File local; preview vía `previewUrl` de Storage) y
   * recupera el resultado por jobId con `recoverJobResult`. Si el backend no
   * conoce el job (JOB_NOT_FOUND), limpia la referencia persistida y marca failed.
   */
  resume: (pending: PendingJob) => void;
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `batch_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const kindFromFile = (file: File): FileKind => (file.type === ACCEPTED_PDF_MIME ? 'pdf' : 'image');

/**
 * Hook que maneja UN batch de archivos procesados con `menuUploaderV2Service.
 * uploadBatchAndProcess` (single-call al backend V4 con `fileUrls: string[]`).
 *
 * Por qué single-call: el agente Claude del backend ve todo el menú a la vez,
 * lo que permite consolidación cross-archivo nativa (sin dedup post-hoc) y
 * elimina el costo de prompt N×. Ver `menuUploaderV2Service.uploadBatchAndProcess`.
 *
 * Semántica preservada para no romper el caller (`SmartMenuOnboarding.tsx`):
 * la API pública (`items`, `totalProgress`, `enqueue`, etc.) es la misma. La
 * cardinalidad de `items` es 0..N: índice 0 = batch principal (enqueue/resume);
 * el resto son batches ADITIVOS lanzados con `appendBatch` desde review.
 *
 * Sin retry automático: si el batch falla, el usuario reintenta con `retry()`.
 * Sin paralelismo: 1 sola operación coordinada (subida paralela interna +
 * 1 invocación callable).
 *
 * @param {UseMultiFileMenuProcessingOptions} [_opts] Aceptado por compat; ignorado.
 * @returns {UseMultiFileMenuProcessingReturn} API pública del hook.
 */
export const useMultiFileMenuProcessing = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _opts?: UseMultiFileMenuProcessingOptions,
): UseMultiFileMenuProcessingReturn => {
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const mountedRef = useRef<boolean>(true);
  // Guarda el último batch PRINCIPAL enviado para soportar `retry()` sin que el
  // caller tenga que conservar los Files. Se limpia con `reset()` y `remove()`.
  const lastBatchRef = useRef<File[] | null>(null);
  // Último pending retomado vía resume(): `retry()` tras un fallo de resume no
  // tiene Files que re-subir, así que reintenta re-consultando este jobId
  // (Seer 3393641983: sin esto, retry quedaba como no-op silencioso).
  const lastResumeRef = useRef<PendingJob | null>(null);
  // Guard POR-BATCH: ids cuya respuesta sigue siendo bienvenida. Reemplaza al
  // par `abortFlagRef` + `currentBatchIdRef` globales (que invalidaban TODO al
  // lanzar un batch nuevo): un append no debe matar al principal ni viceversa.
  // - Lanzar un batch principal (enqueue/resume/retry main) VACÍA el set.
  // - `appendBatch` solo AGREGA su id.
  // - `abortAll`/`reset`/`remove` vacían el set (toda respuesta tardía se ignora).
  const activeBatchIdsRef = useRef<Set<string>>(new Set());
  // Files por batch aditivo: alimenta `retry(appendId)` (relanzar con los
  // mismos archivos). Se limpia al descartar el batch o en reset.
  const appendFilesByIdRef = useRef<Map<string, File[]>>(new Map());

  /**
   * Normaliza el payload del backend (V4 o V3) y marca el batch como completado.
   * Compartido por el camino normal (`runBatch`) y el resume post-reload, para
   * que ambos produzcan exactamente el mismo shape de `UploadQueueItem`.
   */
  const applyBatchResult = useCallback((batchId: string, rawResult: unknown): void => {
    let normalizedResult: BackendAIResult;
    let sections: DetectedSection[] | undefined;
    if (isV4Output(rawResult)) {
      const v4 = rawResult as BackendV4Output;
      normalizedResult = v4ToBackendAIResult(v4);
      sections = prefixV4Sections(batchId, v4);
    } else {
      normalizedResult = rawResult as BackendAIResult;
    }

    setItems((prev) =>
      prev.map((it) =>
        it.id === batchId
          ? {
              ...it,
              status: 'completed',
              result: normalizedResult,
              sections,
              finishedAt: Date.now(),
            }
          : it,
      ),
    );
    // NO limpiar pendingJob aquí: la referencia vive hasta que el usuario
    // publica o reinicia, para que un reload durante la fase de REVIEW pueda
    // recuperar el resultado al instante (spec UX 2026-06-10 §6.2). El caller
    // limpia vía clearPendingJob() en publish-success / restart / discard.
  }, []);

  const runBatch = useCallback(
    async (files: File[], opts?: { append?: boolean; batchId?: string }): Promise<void> => {
      const append = opts?.append === true;
      // `effectiveBatchId` es mutable: el batch PRINCIPAL se re-keyea al jobId
      // en cuanto el service lo emite (onJobStart), para que el prefijo de
      // consolidación — y por lo tanto TODOS los ids de entidades — sea estable
      // entre sesiones. Sin esto, el draft de review guardaba ids con un prefijo
      // aleatorio que jamás re-matcheaba tras un reload (categorías desmarcadas
      // e ids fantasma acumulados en la selección).
      let effectiveBatchId = opts?.batchId ?? generateId();
      if (!append) {
        // Batch principal: supersede TODO lo anterior (respuestas tardías de
        // cualquier batch previo quedan huérfanas del set y se ignoran).
        activeBatchIdsRef.current.clear();
      }
      activeBatchIdsRef.current.add(effectiveBatchId);
      const isActive = (): boolean => activeBatchIdsRef.current.has(effectiveBatchId);

      // Item representante del batch en UI: usamos el PRIMER archivo. El componente
      // padre puede leer `items.length` para conocer cardinalidad, pero también
      // el caller actual (`SmartMenuOnboarding`) renderiza el grid de archivos
      // con la lista original (no la del hook), así que esta simplificación es
      // visualmente neutral en el flow Smart Menu Onboarding.
      const representative = files[0];
      if (!representative) return;

      const totalSizeMB = files.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024);

      const initial: UploadQueueItem = {
        id: effectiveBatchId,
        file: representative,
        kind: kindFromFile(representative),
        sizeMB: Math.round(totalSizeMB * 100) / 100,
        status: 'uploading',
        attempt: 0,
        startedAt: Date.now(),
      };
      // Append: conserva los items existentes (filtrando una versión previa del
      // mismo id — caso retry de un append fallido). Principal: reemplaza todo.
      setItems((prev) => (append ? [...prev.filter((it) => it.id !== effectiveBatchId), initial] : [initial]));

      try {
        const rawResult: unknown = await menuUploaderV2Service.uploadBatchAndProcess(files, {
          // Subidas a Storage completas → arranca la fase LLM. Transicionamos el
          // item a 'analyzing' para que la UI distinga subida (segundos) de
          // análisis (minutos). Guards idénticos al resto del flujo: ignorar si
          // el componente desmontó o el batch dejó de estar activo (abort /
          // batch principal nuevo / descarte).
          onUploadsComplete: () => {
            if (!mountedRef.current || !isActive()) return;
            setItems((prev) => prev.map((it) => (it.id === effectiveBatchId ? { ...it, status: 'analyzing' } : it)));
          },
          // jobId emitido y archivos ya en Storage → persistir la referencia para
          // poder retomar tras un reload de página (ver `resume`). Se persiste
          // aunque haya abort en vuelo: clearPendingJob de abort/reset la limpia.
          // Los batches ADITIVOS no la persisten: pendingJob referencia solo al
          // batch principal (ver tradeoff en `appendBatch`).
          onJobStart: ({ jobId, fileUrls }) => {
            if (append) return;
            savePendingJob({
              jobId,
              startedAt: Date.now(),
              files: files.map((f, idx) => ({
                name: f.name,
                kind: kindFromFile(f),
                sizeMB: Math.round((f.size / (1024 * 1024)) * 100) / 100,
                url: fileUrls[idx] ?? '',
              })),
            });
            // Re-key del batch principal al jobId: el prefijo de consolidación
            // queda estable entre sesiones, así el draft de review re-aplica
            // 1:1 tras un reload (resume() usa este mismo jobId como id).
            if (!mountedRef.current || !isActive()) return;
            const oldId = effectiveBatchId;
            activeBatchIdsRef.current.delete(oldId);
            activeBatchIdsRef.current.add(jobId);
            effectiveBatchId = jobId;
            setItems((prev) => prev.map((it) => (it.id === oldId ? { ...it, id: jobId } : it)));
          },
        });

        if (!mountedRef.current || !isActive()) {
          // El usuario abortó, descartó este batch o lanzó otro principal antes
          // de que llegara la respuesta; descartamos el resultado.
          return;
        }

        applyBatchResult(effectiveBatchId, rawResult);
      } catch (err) {
        if (!mountedRef.current || !isActive()) return;

        const typed = classifyError(err);
        setItems((prev) =>
          prev.map((it) =>
            it.id === effectiveBatchId ? { ...it, status: 'failed', error: typed, finishedAt: Date.now() } : it,
          ),
        );
        // Fallo en firme del batch PRINCIPAL (la recuperación in-flight del
        // service ya se agotó): no queda nada recuperable bajo este jobId. Un
        // append fallido NO toca la referencia: el principal sigue recuperable.
        if (!append) clearPendingJob();
      } finally {
        activeBatchIdsRef.current.delete(effectiveBatchId);
      }
    },
    [applyBatchResult],
  );

  const appendBatch = useCallback(
    (files: File[]): string | null => {
      if (files.length === 0) return null;
      const batchId = generateId();
      appendFilesByIdRef.current.set(batchId, files);
      runBatch(files, { append: true, batchId }).catch(() => undefined);
      return batchId;
    },
    [runBatch],
  );

  const resume = useCallback(
    (pending: PendingJob): void => {
      // El id del batch ES el jobId: mismo prefijo de consolidación que produjo
      // la sesión original (re-keyed en onJobStart), así los ids de entidades
      // son idénticos entre sesiones y el draft de curaduría re-aplica 1:1.
      const batchId = pending.jobId;
      // Resume es un batch PRINCIPAL: supersede todo lo anterior.
      activeBatchIdsRef.current.clear();
      activeBatchIdsRef.current.add(batchId);
      const isActive = (): boolean => activeBatchIdsRef.current.has(batchId);

      const first = pending.files[0];
      if (!first) return;
      const totalSizeMB = pending.files.reduce((acc, f) => acc + (f.sizeMB || 0), 0);

      // Item sintético: no hay `File` local tras el reload; el representante es
      // un stub con el nombre real y el preview viene de la URL de Storage.
      const stubFile = new File([], first.name, {
        type: first.kind === 'pdf' ? ACCEPTED_PDF_MIME : 'image/jpeg',
      });
      const initial: UploadQueueItem = {
        id: batchId,
        file: stubFile,
        kind: first.kind,
        sizeMB: Math.round(totalSizeMB * 100) / 100,
        // Las subidas ya ocurrieron antes del reload: se retoma directo en análisis.
        status: 'analyzing',
        attempt: 0,
        startedAt: pending.startedAt,
        previewUrl: first.url,
      };
      setItems([initial]);
      // Sin Files que re-subir: retry() usará lastResumeRef para re-consultar.
      lastBatchRef.current = null;
      lastResumeRef.current = pending;

      menuUploaderV2Service
        .recoverJobResult(pending.jobId)
        .then((rawResult) => {
          if (!mountedRef.current || !isActive()) return;
          applyBatchResult(batchId, rawResult);
        })
        .catch((err: unknown) => {
          if (!mountedRef.current || !isActive()) return;
          // JOB_NOT_FOUND = el backend no conoce el job: la referencia persistida
          // ya no sirve para nadie. Timeout u otros errores la conservan — un
          // intento posterior dentro del TTL aún puede encontrar el resultado.
          if (err instanceof Error && err.message === 'JOB_NOT_FOUND') {
            clearPendingJob();
          }
          const typed = classifyError(err);
          setItems((prev) =>
            prev.map((it) =>
              it.id === batchId ? { ...it, status: 'failed', error: typed, finishedAt: Date.now() } : it,
            ),
          );
        })
        .finally(() => {
          activeBatchIdsRef.current.delete(batchId);
        });
    },
    [applyBatchResult],
  );

  const enqueue = useCallback(
    (files: File[]): void => {
      if (files.length === 0) return;
      lastBatchRef.current = files;
      // Un batch fresco supersede cualquier resume previo.
      lastResumeRef.current = null;
      // Reemplaza cualquier batch previo (runBatch sin append vacía el set de
      // batches activos: la respuesta del viejo se ignora).
      runBatch(files).catch(() => undefined);
    },
    [runBatch],
  );

  // Cancela y remueve TODO. `id` ignorado (semántica legacy del batch único).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const remove = useCallback((_id: string): void => {
    activeBatchIdsRef.current.clear();
    appendFilesByIdRef.current.clear();
    setItems([]);
    clearPendingJob();
  }, []);

  // Remueve UN batch por id sin tocar el resto (descartar un append fallido o
  // en vuelo). Su respuesta tardía, si llega, se ignora por el guard del set.
  const discardBatch = useCallback((id: string): void => {
    activeBatchIdsRef.current.delete(id);
    appendFilesByIdRef.current.delete(id);
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  // Reintenta un batch. Appends fallidos se relanzan con sus propios archivos
  // (mismo id, sin tocar a los demás items). Cualquier otro id cae al camino
  // legacy: último batch principal o, si lo último fue un resume por jobId
  // (sin Files locales), re-consulta de ese jobId.
  const retry = useCallback(
    (id: string): void => {
      const appendFiles = appendFilesByIdRef.current.get(id);
      if (appendFiles && appendFiles.length > 0) {
        runBatch(appendFiles, { append: true, batchId: id }).catch(() => undefined);
        return;
      }
      const last = lastBatchRef.current;
      if (last && last.length > 0) {
        runBatch(last).catch(() => undefined);
        return;
      }
      const lastResume = lastResumeRef.current;
      if (lastResume) {
        resume(lastResume);
      }
    },
    [runBatch, resume],
  );

  const abortAll = useCallback((): void => {
    activeBatchIdsRef.current.clear();
    setItems((prev) =>
      prev.map((it) =>
        it.status === 'queued' || it.status === 'uploading' || it.status === 'analyzing'
          ? { ...it, status: 'cancelled', finishedAt: Date.now() }
          : it,
      ),
    );
    // pendingJob se conserva: el backend puede completar el job igualmente y
    // el usuario podría recuperar el resultado más tarde (banner de recovery).
  }, []);

  const reset = useCallback((): void => {
    activeBatchIdsRef.current.clear();
    appendFilesByIdRef.current.clear();
    lastBatchRef.current = null;
    lastResumeRef.current = null;
    setItems([]);
    clearPendingJob();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activeBatchIdsRef.current.clear();
    };
  }, []);

  const totalProgress = useMemo(
    () => ({
      done: items.filter((it) => it.status === 'completed').length,
      total: items.length,
      failed: items.filter((it) => it.status === 'failed').length,
    }),
    [items],
  );

  const isProcessing = useMemo(
    () => items.some((it) => it.status === 'queued' || it.status === 'uploading' || it.status === 'analyzing'),
    [items],
  );

  return {
    items,
    totalProgress,
    isProcessing,
    enqueue,
    appendBatch,
    remove,
    retry,
    discardBatch,
    abortAll,
    reset,
    resume,
  };
};
